/* eslint-disable react/prop-types */
import React from "react";
import { shape, object } from "prop-types";
import { Text, Stack, Shelf, TextLink, Button } from "@chaser/components";
import { CreditCard } from "lucide-react";
import { useHistory } from "react-router";
import { roles, Modules } from "../../../../../util/constants";
import CancelSubscriptionModal from "./CancelSubscriptionModal";
import useModal from "../../../../../hooks/useModal";
import useOrganisationUtils from "../../../../../hooks/useOrganisationUtils";
import useManageSubscription from "../../../../../hooks/useManageSubscription";
import RoleGate from "@/components/FeatureGate/RoleGate";
import {
  buildFallbackSummary,
  formatIncludedUsersBreakdownLabel,
  getBillingCadenceMeta,
  getModuleIncludedUsersCount,
  getFormattedNextBillDate,
  getPaymentLabels,
  prettifyTitle,
  toAmount,
} from "./utils";
import {
  CLICKABLE_MODULE_SUMMARY_IDS,
  MODULE_QUERY_PARAM,
  SUBSCRIPTION_ADDONS_PATH,
} from "./constants";
import TrialModulesSection from "./TrialModulesSection";
import ExpiredTrialsSection from "./ExpiredTrialsSection";
import {
  getScheduledModuleChangeDate,
  isScheduledRemovedChildAddon,
} from "./scheduledChanges";

import style from "./ActiveSubscription.module.scss";

const ActiveSubscription = ({ subscription }) => {
  const history = useHistory();
  const showModal = useModal();
  const { formatCurrency } = useOrganisationUtils();
  const manageSubscription = useManageSubscription();
  const summary = subscription?.summary || buildFallbackSummary(subscription);
  const paymentMethod = summary?.paymentMethod || null;
  const { cardLabel, expiryLabel } = getPaymentLabels(paymentMethod);

  const moduleSubscriptions = subscription?.moduleSubscriptions || [];
  const receivablesModuleSubscription = moduleSubscriptions.find(
    (module) => String(module?.id || "") === Modules.RECEIVABLES,
  );
  const receivablesSubscriberId = String(
    receivablesModuleSubscription?.subscriber || "",
  ).trim();
  const subscriptionSubscriber = subscription?.subscriber || null;
  const subscriptionSubscriberId = String(
    subscriptionSubscriber?._id || "",
  ).trim();
  const subscriberProfile =
    receivablesSubscriberId &&
    receivablesSubscriberId === subscriptionSubscriberId
      ? subscriptionSubscriber?.profile || null
      : null;
  const subscriberName = [
    subscriberProfile?.firstName,
    subscriberProfile?.lastName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
  const trialModules = moduleSubscriptions.filter(
    (module) => module?.isInTrial,
  );
  const hasAnyActivePaidModule = moduleSubscriptions.some(
    (module) =>
      module?.isActive &&
      !module?.isInTrial &&
      String(module?.id || "") !== Modules.ORGANISATION,
  );

  const expiredTrialModules = moduleSubscriptions.filter((module) => {
    const now = new Date();
    const trialEndDate = module?.trialEndsAt
      ? new Date(module.trialEndsAt)
      : null;
    return (
      !module?.isActive &&
      trialEndDate &&
      !Number.isNaN(trialEndDate.getTime()) &&
      trialEndDate < now
    );
  });
  const hasOnlyTrialModules =
    trialModules.length > 0 &&
    moduleSubscriptions.every(
      (module) => module?.isInTrial && !module?.isActive,
    );

  const canShowPaymentPanel =
    subscription?.subscriptionService !== "xero" && !hasOnlyTrialModules;

  const billingCadence = getBillingCadenceMeta(summary, moduleSubscriptions);
  const isMixedCadence = billingCadence.label === "Mixed";

  const openBillingPortal = () => {
    manageSubscription();
  };

  const openPlansAndAddons = (moduleName) => {
    const defaultModuleId = moduleName || trialModules[0]?.id;
    const query = defaultModuleId
      ? `?${MODULE_QUERY_PARAM}=${encodeURIComponent(defaultModuleId)}`
      : "";
    history.push(`${SUBSCRIPTION_ADDONS_PATH}${query}`);
  };

  const moduleSummaryIds = new Set(CLICKABLE_MODULE_SUMMARY_IDS);

  const openModuleInPlansAddons = (moduleName) => {
    history.push(
      `${SUBSCRIPTION_ADDONS_PATH}?${MODULE_QUERY_PARAM}=${encodeURIComponent(moduleName)}`,
    );
  };

  const fallbackCadenceLabel = String(summary?.billed || "")
    .toLowerCase()
    .includes("annual")
    ? "Annual"
    : "Monthly";

  const getLineItemCadenceLabel = (itemId) => {
    if (String(itemId || "") === Modules.ORGANISATION) return "Monthly";

    const matchedModule = moduleSubscriptions.find(
      (module) => String(module?.id || "") === String(itemId || ""),
    );

    if (matchedModule?.isInTrial) return "Trial";
    if (matchedModule?.period === "year") return "Annual";
    if (matchedModule?.period === "month") return "Monthly";

    return fallbackCadenceLabel;
  };

  const getLineItemCadenceKey = (itemId) => {
    const cadenceLabel = String(
      getLineItemCadenceLabel(itemId) || "",
    ).toLowerCase();
    if (cadenceLabel === "annual") return "annual";
    if (cadenceLabel === "monthly") return "monthly";

    const fallbackCadenceKey = String(fallbackCadenceLabel || "").toLowerCase();
    return fallbackCadenceKey === "annual" ? "annual" : "monthly";
  };

  const getLineItemTitle = (item) => {
    const baseTitle = prettifyTitle(item?.title);
    if (String(item?.id || "") === Modules.ORGANISATION) {
      return baseTitle;
    }

    const matchedModule = moduleSubscriptions.find(
      (module) => String(module?.id || "") === String(item?.id || ""),
    );

    const moduleName = String(matchedModule?.moduleName || "").trim();
    const rawPlanName = String(matchedModule?.planName || "").trim();
    const isDuplicateModulePlanName =
      rawPlanName &&
      moduleName &&
      rawPlanName.toLowerCase() === moduleName.toLowerCase();

    let resolvedPlanName = isDuplicateModulePlanName ? "" : rawPlanName;

    if (!resolvedPlanName && String(item?.id || "") === Modules.RECEIVABLES) {
      resolvedPlanName = String(
        subscription?.plan?.title || subscription?.plan?.name || "",
      ).trim();
    }

    if (!resolvedPlanName) {
      return baseTitle;
    }

    if (baseTitle.toLowerCase().includes(resolvedPlanName.toLowerCase())) {
      return baseTitle;
    }

    return `${baseTitle} - ${resolvedPlanName}`;
  };

  const shouldHideLineItemAmount = (item) =>
    String(item?.id || "") === Modules.ORGANISATION && toAmount(item) === 0;

  const chargeLineItems = (summary.lineItems || []).filter((item) => {
    const matchedModule = moduleSubscriptions.find(
      (module) => String(module?.id || "") === String(item?.id || ""),
    );

    // Exclude if in trial or has expired trial
    if (matchedModule?.isInTrial) return false;

    const isExpiredTrial = expiredTrialModules.some(
      (expired) => String(expired?.id || "") === String(item?.id || ""),
    );

    return !isExpiredTrial;
  });

  const fallbackChargeTotalAmount = chargeLineItems.reduce((total, item) => {
    const lineAmount = toAmount(item);
    const childrenAmount = (item.children || []).reduce(
      (subTotal, child) => subTotal + toAmount(child),
      0,
    );

    return total + lineAmount + childrenAmount;
  }, 0);

  const chargeTotalAmount =
    typeof summary?.totalAmount === "number"
      ? summary.totalAmount
      : fallbackChargeTotalAmount;

  const chargeTotalsByCadence = chargeLineItems.reduce(
    (acc, item) => {
      const cadenceKey = getLineItemCadenceKey(item.id);
      const lineAmount = toAmount(item);
      const childrenAmount = (item.children || []).reduce(
        (subTotal, child) => subTotal + toAmount(child),
        0,
      );
      const itemTotal = lineAmount + childrenAmount;

      acc[cadenceKey] += itemTotal;
      return acc;
    },
    { monthly: 0, annual: 0 },
  );

  const getChargeLineItemTotal = (item) => {
    const lineAmount = toAmount(item);
    const childrenAmount = (item.children || []).reduce(
      (subTotal, child) => subTotal + toAmount(child),
      0,
    );

    return lineAmount + childrenAmount;
  };

  const getCadenceKeyFromModule = (module) => {
    if (module?.period === "year") return "annual";
    if (module?.period === "month") return "monthly";
    return null;
  };

  const getNextChargeSubscriptionName = (lineItem, module) => {
    const matchingModule =
      module ||
      moduleSubscriptions.find(
        (candidate) =>
          String(candidate?.id || "") === String(lineItem?.id || ""),
      );

    if (matchingModule?.moduleName) {
      return String(matchingModule.moduleName).trim();
    }

    return prettifyTitle(lineItem?.title || "Subscription");
  };

  const byAscendingDate = (left, right) => {
    const leftTime =
      left.nextBillingDate?.getTime?.() || Number.POSITIVE_INFINITY;
    const rightTime =
      right.nextBillingDate?.getTime?.() || Number.POSITIVE_INFINITY;

    if (leftTime !== rightTime) {
      return leftTime - rightTime;
    }

    return String(left.subscriptionName || "").localeCompare(
      String(right.subscriptionName || ""),
    );
  };

  const buildChargeCandidatesByCadence = (cadenceKey) =>
    moduleSubscriptions
      .filter((module) => {
        if (!module?.isActive || module?.isInTrial) return false;
        return getCadenceKeyFromModule(module) === cadenceKey;
      })
      .map((module) => {
        const matchingLineItem = chargeLineItems.find(
          (item) => String(item?.id || "") === String(module?.id || ""),
        );
        const parsedNextBillingDate = module?.nextBillingDate
          ? new Date(module.nextBillingDate)
          : null;
        const nextBillingDate =
          parsedNextBillingDate &&
          !Number.isNaN(parsedNextBillingDate.getTime())
            ? parsedNextBillingDate
            : null;
        const fallbackModuleAmount = Number(module?.amountMinor || 0) / 100;

        return {
          cadenceKey,
          label: cadenceKey === "annual" ? "Annual" : "Monthly",
          amount: matchingLineItem
            ? getChargeLineItemTotal(matchingLineItem)
            : fallbackModuleAmount,
          nextBillingDate,
          dateLabel: nextBillingDate
            ? getFormattedNextBillDate({ nextBillDate: nextBillingDate })
            : "-",
          subscriptionName: getNextChargeSubscriptionName(
            matchingLineItem,
            module,
          ),
          referenceId: String(
            module?.subscriptionId || matchingLineItem?.id || "",
          ).trim(),
        };
      })
      .filter((entry) => entry.nextBillingDate)
      .sort(byAscendingDate);

  const buildNextChargeByCadence = (cadenceKey) => {
    const chargeCandidates = buildChargeCandidatesByCadence(cadenceKey);
    const nextBillableCharge = chargeCandidates.find(
      (entry) => entry.amount > 0,
    );

    if (nextBillableCharge) {
      return nextBillableCharge;
    }

    return {
      cadenceKey,
      label: cadenceKey === "annual" ? "Annual" : "Monthly",
      amount: 0,
      dateLabel: "-",
      subscriptionName: "",
      referenceId: "",
    };
  };

  const resolvedPrimaryCadenceKey =
    String(billingCadence.label || "").toLowerCase() === "annual"
      ? "annual"
      : "monthly";

  const nextChargeBreakdown = isMixedCadence
    ? ["monthly", "annual"]
        .map(buildNextChargeByCadence)
        .filter((entry) => entry.amount > 0)
    : [buildNextChargeByCadence(resolvedPrimaryCadenceKey)].filter(
        (entry) => entry.amount > 0,
      );

  const totalRows = isMixedCadence
    ? [
        {
          label: "Monthly total",
          amount: chargeTotalsByCadence.monthly,
        },
        {
          label: "Annual total",
          amount: chargeTotalsByCadence.annual,
        },
      ].filter((entry) => entry.amount > 0)
    : [
        {
          label:
            billingCadence.label === "Annual" ||
            billingCadence.label === "Monthly"
              ? `Total (${billingCadence.label})`
              : "Total",
          amount: chargeTotalAmount,
        },
      ];

  const plan = subscription?.plan;
  const scheduledAddonChanges = summary?.scheduledChanges || [];

  const isScheduledRemovedChild = (moduleId, child) =>
    isScheduledRemovedChildAddon({
      moduleId,
      child,
      scheduledAddonChanges,
    });

  const hasScheduledChanges = scheduledAddonChanges.length > 0;
  const scheduledChangeDate = getScheduledModuleChangeDate({
    plan,
    scheduledAddonChanges,
    summary,
  });

  const removedChildAdjustmentAmount = chargeLineItems.reduce((total, item) => {
    const removedChildrenAmount = (item.children || []).reduce(
      (subTotal, child) => {
        if (!isScheduledRemovedChild(item.id, child)) return subTotal;
        return subTotal + toAmount(child);
      },
      0,
    );

    return total + removedChildrenAmount;
  }, 0);

  const adjustedPrice =
    removedChildAdjustmentAmount > 0
      ? -removedChildAdjustmentAmount
      : scheduledAddonChanges.reduce((total, addon) => {
          const overagesDelta = addon.scheduledOverages - addon.currentOverages;
          return total + (overagesDelta * addon.price) / 100;
        }, 0);

  const adjustedAmountText =
    adjustedPrice === 0
      ? formatCurrency(0, summary.currency)
      : `${adjustedPrice > 0 ? "+" : "-"}${formatCurrency(
          Math.abs(adjustedPrice),
          summary.currency,
        )}`;

  return (
    <div className={style.layoutGrid}>
      <Stack gap="medium" className={style.leftPanel}>
        <Shelf className={style.headerRow}>
          <Text fontSize="heading.3" color="Dark Neutral 1">
            Subscription summary
          </Text>
          {summary.status === "Active" && (
            <span className={style.statusPill}>{summary.status}</span>
          )}
        </Shelf>

        {hasOnlyTrialModules ? (
          <>
            <Stack className={style.trialCtaBox}>
              <Text fontSize="heading.4" color="Dark Neutral 1">
                Keep your trial modules running
              </Text>
              <Text color="Mid Neutral 1">
                Subscribe now to continue without interruption.
              </Text>
              <Button
                className={style.subscribeNowButton}
                onClick={openPlansAndAddons}
              >
                Subscribe now
              </Button>
            </Stack>

            <TrialModulesSection
              trialModules={trialModules}
              moduleSummaryIds={moduleSummaryIds}
              onOpenModule={openModuleInPlansAddons}
              style={style}
            />

            {expiredTrialModules.length > 0 && (
              <ExpiredTrialsSection
                expiredTrialModules={expiredTrialModules}
                moduleSummaryIds={moduleSummaryIds}
                onOpenModule={openModuleInPlansAddons}
                onSubscribeNow={openPlansAndAddons}
                style={style}
              />
            )}
          </>
        ) : (
          <>
            {hasScheduledChanges && scheduledChangeDate && (
              <div className={style.scheduledChangesBanner}>
                <Text fontWeight="bold">
                  This subscription is scheduled for change on{" "}
                  {scheduledChangeDate}.
                </Text>
              </div>
            )}

            <div className={style.statGrid}>
              <Stack className={style.statCard}>
                <Text
                  color="Mid Neutral 1"
                  fontSize="12px"
                  className={style.metaLabel}
                >
                  BILLING CADENCE
                </Text>
                <Text>{billingCadence.label}</Text>
                <Text color="Mid Neutral 1">{billingCadence.description}</Text>
              </Stack>
              <Stack className={style.statCard}>
                <Text
                  color="Mid Neutral 1"
                  fontSize="12px"
                  className={style.metaLabel}
                >
                  NEXT CHARGE
                </Text>
                {nextChargeBreakdown.length > 0 ? (
                  <div className={style.nextChargeRows}>
                    {nextChargeBreakdown.map((entry) => (
                      <div
                        key={entry.cadenceKey}
                        className={style.nextChargeRow}
                      >
                        <Shelf className={style.nextChargeRowHeader}>
                          <Stack gap="xsmall">
                            <Text color="Mid Neutral 1">
                              Due on <strong>{entry.dateLabel}</strong>
                            </Text>
                            <Shelf
                              className={style.nextChargeNameRow}
                              gap="xxsmall"
                            >
                              <Text fontWeight="bold">
                                {entry.subscriptionName}
                              </Text>
                            </Shelf>
                            {entry.referenceId && (
                              <Text
                                color="Mid Neutral 1"
                                fontSize="10px"
                                className={style.nextChargeReference}
                              >
                                Ref {entry.referenceId}
                              </Text>
                            )}
                          </Stack>
                          <Shelf className={style.nextChargeValue}>
                            <span
                              className={`${style.cadenceTag} ${
                                entry.cadenceKey === "annual"
                                  ? style.cadenceTagAnnual
                                  : style.cadenceTagMonthly
                              }`}
                            >
                              {entry.label}
                            </span>
                            <Text
                              fontWeight="bold"
                              className={style.nextChargeAmount}
                            >
                              {formatCurrency(entry.amount, summary.currency)}
                            </Text>
                          </Shelf>
                        </Shelf>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Text color="Mid Neutral 1">
                    Due on <strong>-</strong>
                  </Text>
                )}
              </Stack>
            </div>

            {chargeLineItems.length > 0 && (
              <div className={style.billingBox}>
                <div className={style.breakdownBox}>
                  <Text
                    color="Mid Neutral 1"
                    fontSize="12px"
                    className={`${style.metaLabel} ${style.breakdownTitle}`}
                  >
                    BREAKDOWN
                  </Text>

                  <div className={style.lineItems}>
                    {chargeLineItems.map((item) => {
                      const matchedModule = moduleSubscriptions.find(
                        (module) =>
                          String(module?.id || "") === String(item?.id || ""),
                      );
                      const includedUsersLabel =
                        formatIncludedUsersBreakdownLabel(
                          getModuleIncludedUsersCount(
                            subscription,
                            matchedModule,
                          ),
                        );
                      const childItems = item.children || [];
                      const hasChildSection =
                        childItems.length > 0 || Boolean(includedUsersLabel);

                      return (
                        <div key={item.id} className={style.lineItemBlock}>
                          <div className={style.lineRow}>
                            <Stack gap="xsmall">
                              {moduleSummaryIds.has(item.id) ? (
                                <TextLink
                                  monochrome
                                  onClick={() =>
                                    openModuleInPlansAddons(item.id)
                                  }
                                >
                                  {getLineItemTitle(item)}
                                </TextLink>
                              ) : (
                                <Text fontWeight="bold">
                                  {getLineItemTitle(item)}
                                </Text>
                              )}
                              <Text
                                color="Mid Neutral 1"
                                className={style.lineSubtitle}
                              >
                                {getLineItemCadenceLabel(item.id)}
                              </Text>
                            </Stack>
                            <Text>
                              {shouldHideLineItemAmount(item)
                                ? ""
                                : formatCurrency(
                                    toAmount(item),
                                    summary.currency,
                                  )}
                            </Text>
                          </div>

                          {hasChildSection && (
                            <div className={style.childLines}>
                              {childItems.map((child) => {
                                const isScheduledRemoval =
                                  isScheduledRemovedChild(item.id, child);
                                return (
                                  <div
                                    key={child.id}
                                    className={`${
                                      isScheduledRemoval
                                        ? style.scheduledChildRow
                                        : style.childRow
                                    }`}
                                  >
                                    <Text color="Mid Neutral 1">
                                      {child.description ||
                                        prettifyTitle(child.title)}
                                    </Text>
                                    <Text color="Mid Neutral 1">
                                      {formatCurrency(
                                        toAmount(child),
                                        summary.currency,
                                      )}
                                    </Text>
                                  </div>
                                );
                              })}

                              {includedUsersLabel && (
                                <div className={style.includedChildRow}>
                                  <Text color="Mid Neutral 1">
                                    {includedUsersLabel}
                                  </Text>
                                  <Text color="Mid Neutral 1">Free</Text>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {totalRows.map((entry) => (
                    <Shelf key={entry.label} className={style.totalRow}>
                      <Text fontWeight="bold">{entry.label}</Text>
                      <Text fontWeight="bold">
                        {formatCurrency(entry.amount, summary.currency)}
                      </Text>
                    </Shelf>
                  ))}
                  {hasScheduledChanges && scheduledAddonChanges.length > 0 && (
                    <Shelf className={style.totalRow}>
                      <Text fontWeight="bold">Adjusted</Text>
                      <Text
                        fontWeight="bold"
                        color={adjustedPrice > 0 ? "Green" : "Red"}
                      >
                        {adjustedAmountText}
                      </Text>
                    </Shelf>
                  )}
                </div>
              </div>
            )}

            <TrialModulesSection
              trialModules={trialModules}
              moduleSummaryIds={moduleSummaryIds}
              onOpenModule={openModuleInPlansAddons}
              style={style}
            />

            {expiredTrialModules.length > 0 && (
              <ExpiredTrialsSection
                expiredTrialModules={expiredTrialModules}
                moduleSummaryIds={moduleSummaryIds}
                onOpenModule={openModuleInPlansAddons}
                onSubscribeNow={openPlansAndAddons}
                style={style}
              />
            )}
          </>
        )}

        <RoleGate roles={[roles.owner]}>
          {subscription.isSubscribed && hasAnyActivePaidModule && (
            <TextLink
              monochrome
              className={style.cancelLink}
              onClick={() => showModal(<CancelSubscriptionModal />)}
            >
              Cancel subscription
            </TextLink>
          )}
        </RoleGate>
      </Stack>

      {canShowPaymentPanel && (
        <Stack className={style.rightPanel}>
          <Stack className={style.paymentSection}>
            <Text
              color="Mid Neutral 1"
              fontSize="12px"
              className={style.sectionTitle}
            >
              PAYMENT METHOD
            </Text>
            <Shelf className={style.paymentCard}>
              <Shelf className={style.paymentCardMain}>
                <span className={style.paymentIcon}>
                  <CreditCard size={18} strokeWidth={2.1} />
                </span>
                <Stack className={style.paymentCardText}>
                  <Text>{cardLabel}</Text>
                  {expiryLabel && (
                    <Text color="Mid Neutral 1">{expiryLabel}</Text>
                  )}
                </Stack>
              </Shelf>
              <RoleGate roles={[roles.billing, roles.owner]}>
                <Button
                  appearance="secondary"
                  className={`${style.panelButton} ${style.updateButton}`}
                  onClick={openBillingPortal}
                >
                  Update
                </Button>
              </RoleGate>
            </Shelf>
          </Stack>

          <Stack className={style.paymentSection}>
            <Text
              color="Mid Neutral 1"
              fontSize="12px"
              className={style.sectionTitle}
            >
              SUBSCRIBER
            </Text>
            <Stack gap="xsmall">
              {subscriberName ? (
                <Text>{subscriberName}</Text>
              ) : (
                <Text color="Mid Neutral 1">Subscriber unavailable</Text>
              )}
            </Stack>
          </Stack>
        </Stack>
      )}
    </div>
  );
};

ActiveSubscription.propTypes = {
  subscription: shape({
    summary: object,
  }).isRequired,
};

export default ActiveSubscription;
